import React, { useState } from 'react'
import { Box, Typography, Tooltip, IconButton } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '../../constants/layout'

export const SideBarPage: React.FC<{
  title: string,
  headerContent?: React.ReactNode,
  sidebar: React.ReactNode,
  children: React.ReactNode,
  /** Shown in the collapsed rail under the open button: the sidebar's icons without their labels. */
  collapsedContent?: React.ReactNode,
  /** Controls the sidebar from the page; omit both to let it manage itself. */
  sidebarOpen?: boolean,
  onSidebarOpenChange?: (open: boolean) => void,
}> = ({
  title,
  headerContent,
  sidebar,
  children,
  collapsedContent,
  sidebarOpen,
  onSidebarOpenChange,
}) => {
  const [ownSidebarVisible, setOwnSidebarVisible] = useState(true)
  const sidebarVisible = sidebarOpen ?? ownSidebarVisible
  const setSidebarVisible = (open: boolean) => {
    setOwnSidebarVisible(open)
    onSidebarOpenChange?.(open)
  }
  return (
    <Box sx={{
      display: 'flex',
      height: 'calc(100vh - 64px)',
      overflow: 'hidden',
    }}>
      {
        sidebarVisible ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 3,
            pt: 2.5,
            pb: 1.5,
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            height: '100%',
            overflow: 'hidden',
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: '0.75rem',
              }}>
                { title }
              </Typography>
              <Tooltip title="Collapse sidebar">
                <IconButton
                  size="small"
                  onClick={() => setSidebarVisible(false)}
                  sx={{
                    ml: 1,
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {headerContent}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minWidth: 0
              }}
            >
              {sidebar}
            </Box>
          </Box>
        ) : (
          <Box
            onClick={() => setSidebarVisible(true)}
            sx={{
              width: SIDEBAR_COLLAPSED_WIDTH,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: 1.5,
              borderRight: '1px solid',
              borderColor: 'divider',
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 136, 229, 0.06)' : 'rgba(30, 136, 229, 0.04)',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 136, 229, 0.1)' : 'rgba(30, 136, 229, 0.07)',
              },
            }}
          >
            <Tooltip title="Open" placement="right">
              <IconButton
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                  }
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {collapsedContent && (
              // Its own clicks stay its own; the rest of the rail opens the sidebar.
              <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1, cursor: 'default' }}>
                {collapsedContent}
              </Box>
            )}
          </Box>
        )
      }

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        { children }
      </Box>

    </Box>
  )
}

export default SideBarPage
